import {
  type UseQueryResult,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useScopedQueryKey } from '../../../hooks/useScopedQueryKey';
import { api, apiEndpoints } from '../../../utils/apiService';
import {
  type AreaBlackout,
  type Channel,
  type DiningArea,
  type EventTypeOption,
  type GooglePlaceLink,
  type ListResponse,
  type NotificationRule,
  type NotificationTarget,
  type SpecialHour,
  type StoreHour,
  type StoreHoursData,
  type StoreInfoData,
  type VoiceOption,
  type VoiceSelection,
  STAFF_MESSAGE,
  normalizeQuestions,
} from './model';

/**
 * Store Info V2 data layer. One React Query per endpoint, keyed with the same
 * scoped keys the rest of the app uses so caches stay shared. Each section
 * reads a `SectionQuery` (what `QueryGate` accepts) and calls the matching
 * `use*Actions()` hook for writes.
 */

/** `useScopedQueryKey` with a stable identity, so action hooks stay memoised. */
function useKey(
  base: string,
  ...parts: Array<string | number | boolean | null | undefined>
) {
  const key = useScopedQueryKey(base, ...parts);
  const serialized = JSON.stringify(key);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => key, [serialized]);
}

export interface SectionQuery<T> {
  status: 'pending' | 'error' | 'success';
  data: T | undefined;
  error: string | null;
  refetch: () => void;
}

export function toSectionQuery<T>(
  q: Pick<UseQueryResult<T>, 'status' | 'data' | 'error' | 'refetch'>,
  fallback: string,
): SectionQuery<T> {
  return {
    status: q.status,
    data: q.data,
    error:
      q.status === 'error'
        ? q.error instanceof Error && q.error.message
          ? q.error.message
          : fallback
        : null,
    refetch: () => void q.refetch(),
  };
}

/** Combines several queries into one gate; pending wins over error. */
export function combineQueries<T>(
  queries: Array<SectionQuery<unknown>>,
  build: () => T,
  fallback: string,
): SectionQuery<T> {
  const refetch = () => queries.forEach((q) => q.refetch());
  if (queries.some((q) => q.status === 'pending'))
    return { status: 'pending', data: undefined, error: null, refetch };
  const failed = queries.find((q) => q.status === 'error');
  if (failed)
    return {
      status: 'error',
      data: undefined,
      error: failed.error || fallback,
      refetch,
    };
  return { status: 'success', data: build(), error: null, refetch };
}

// ---------------------------------------------------------------------------
// Response helpers
// ---------------------------------------------------------------------------

export async function readError(
  resp: Response | null | undefined,
  fallback: string,
): Promise<string> {
  if (!resp) return fallback;
  const data = await resp.json().catch(() => null);
  if (!data || typeof data !== 'object') return fallback;
  for (const key of ['detail', 'message', 'error'] as const) {
    const v = data[key];
    if (typeof v === 'string' && v) return v;
    if (v && typeof v === 'object') {
      const first = Object.values(v).flat()[0];
      if (typeof first === 'string' && first) return first;
    }
  }
  const field = Object.values(data).flat()[0];
  return typeof field === 'string' && field ? field : fallback;
}

async function ensureOk(
  resp: Response | null | undefined,
  fallback: string,
): Promise<Response> {
  if (!resp?.ok) throw new Error(await readError(resp, fallback));
  return resp;
}

/** Tolerates empty bodies (204) and non-JSON success responses. */
async function readJson<T>(resp: Response, fallback: T): Promise<T> {
  return (await resp.json().catch(() => fallback)) as T;
}

const asList = <T>(data: unknown): T[] => {
  if (Array.isArray(data)) return data as T[];
  const results = (data as { results?: unknown } | null)?.results;
  return Array.isArray(results) ? (results as T[]) : [];
};

// ---------------------------------------------------------------------------
// Store info
// ---------------------------------------------------------------------------

export function useStoreInfoQuery(
  fallback = 'Store info could not be loaded.',
) {
  const key = useKey('store-info');
  const q = useQuery({
    queryKey: key,
    queryFn: async (): Promise<StoreInfoData> => {
      const resp = await ensureOk(
        await api.get(apiEndpoints.getStoreInfo()),
        fallback,
      );
      return resp.json();
    },
  });
  return toSectionQuery(q, fallback);
}

export function useStoreActions() {
  const queryClient = useQueryClient();
  const key = useKey('store-info');
  const update = useCallback(
    async (patch: Partial<StoreInfoData>): Promise<StoreInfoData> => {
      const current = queryClient.getQueryData<StoreInfoData>(key);
      if (!current?.store_id) throw new Error('Store info is not loaded yet.');
      const body: Partial<StoreInfoData> = { ...patch };
      if ('event_inquiry_questions' in body)
        body.event_inquiry_questions = normalizeQuestions(
          body.event_inquiry_questions,
        );
      if ('catering_inquiry_questions' in body)
        body.catering_inquiry_questions = normalizeQuestions(
          body.catering_inquiry_questions,
        );
      const resp = await ensureOk(
        await api.put(apiEndpoints.updateOwnStore(current.store_id), body),
        'Could not save. Try again.',
      );
      const saved = await readJson<Partial<StoreInfoData>>(resp, {});
      const next = { ...current, ...body, ...saved } as StoreInfoData;
      queryClient.setQueryData(key, next);
      void queryClient.invalidateQueries({ queryKey: key });
      window.dispatchEvent(new Event('store-settings-updated'));
      return next;
    },
    [queryClient, key],
  );
  return useMemo(() => ({ update }), [update]);
}

// ---------------------------------------------------------------------------
// Hours
// ---------------------------------------------------------------------------

export function useStoreHoursQuery() {
  const key = useKey('store-hours');
  const fallback = 'Store hours could not be loaded.';
  const q = useQuery({
    queryKey: key,
    queryFn: async (): Promise<StoreHoursData> => {
      const resp = await ensureOk(
        await api.get(apiEndpoints.getStoreHours()),
        fallback,
      );
      return resp.json();
    },
  });
  return toSectionQuery(q, fallback);
}

export function useSpecialHoursQuery() {
  const key = useKey('special-hours');
  const fallback = 'Special dates could not be loaded.';
  const q = useQuery({
    queryKey: key,
    queryFn: async (): Promise<SpecialHour[]> => {
      const resp = await ensureOk(
        await api.get(apiEndpoints.getSpecialHours(true)),
        fallback,
      );
      return asList<SpecialHour>(await resp.json());
    },
  });
  return toSectionQuery(q, fallback);
}

export function useHoursActions() {
  const queryClient = useQueryClient();
  const hoursKey = useKey('store-hours');
  const infoKey = useKey('store-info');
  const specialKey = useKey('special-hours');

  return useMemo(() => {
    const afterHours = async (resp: Response) => {
      const data = await readJson<StoreHoursData | null>(resp, null);
      if (data) queryClient.setQueryData(hoursKey, data);
      void queryClient.invalidateQueries({ queryKey: hoursKey });
      void queryClient.invalidateQueries({ queryKey: infoKey });
    };
    const specialPayload = (sh: Omit<SpecialHour, 'id'>) => ({
      date: sh.date,
      is_closed: sh.is_closed,
      holiday_name: sh.holiday_name || null,
      special_info: (sh.special_info ?? '').trim(),
      opening_time: sh.is_closed ? null : sh.opening_time,
      closing_time: sh.is_closed ? null : sh.closing_time,
    });
    const afterSpecial = () =>
      queryClient.invalidateQueries({ queryKey: specialKey });

    return {
      setSource: async (manual: boolean) => {
        const resp = await ensureOk(
          await api.patch(apiEndpoints.updateStoreHours(), {
            use_manual_hours: manual,
          }),
          'Could not change where hours come from.',
        );
        await afterHours(resp);
      },
      saveWeekly: async (hours: StoreHour[]) => {
        const resp = await ensureOk(
          await api.put(apiEndpoints.updateStoreHours(), { hours }),
          'Could not save store hours.',
        );
        await afterHours(resp);
      },
      createSpecial: async (sh: Omit<SpecialHour, 'id'>) => {
        await ensureOk(
          await api.post(apiEndpoints.createSpecialHours(), specialPayload(sh)),
          'Could not save the special date.',
        );
        await afterSpecial();
      },
      updateSpecial: async (id: number, sh: Omit<SpecialHour, 'id'>) => {
        await ensureOk(
          await api.patch(
            apiEndpoints.updateSpecialHours(id),
            specialPayload(sh),
          ),
          'Could not save the special date.',
        );
        await afterSpecial();
      },
      deleteSpecial: async (id: number) => {
        await ensureOk(
          await api.delete(apiEndpoints.deleteSpecialHours(id)),
          'Could not delete the special date.',
        );
        await afterSpecial();
      },
      /** Google linking rewrites weekly hours server-side. */
      refreshHours: () => {
        void queryClient.invalidateQueries({ queryKey: hoursKey });
        void queryClient.invalidateQueries({ queryKey: infoKey });
      },
    };
  }, [queryClient, hoursKey, infoKey, specialKey]);
}

// ---------------------------------------------------------------------------
// Agent voice
// ---------------------------------------------------------------------------

export function isVoicePlatformLabel(value: string): boolean {
  const normalized = value.toLowerCase().replace(/[^a-z0-9]+/g, '');
  return (
    normalized === 'retell' ||
    normalized === 'elevenlabs' ||
    normalized === '11labs'
  );
}

/** Strips voice-platform names from the catalog before it reaches the UI. */
function customerFacingVoice(v: VoiceOption): VoiceOption {
  const source = v.source_name?.trim();
  return {
    ...v,
    source_name: source && !isVoicePlatformLabel(source) ? source : undefined,
    voice_type: isVoicePlatformLabel(v.voice_type || '') ? '' : v.voice_type,
    traits: (v.traits || []).filter((t) => t && !isVoicePlatformLabel(t)),
  };
}

export function useVoiceSelectionQuery() {
  const key = useKey('store-agent-voice');
  const fallback = 'The current voice could not be loaded.';
  const q = useQuery({
    queryKey: key,
    queryFn: async (): Promise<VoiceSelection> => {
      const resp = await ensureOk(
        await api.get(apiEndpoints.getStoreAgentVoice()),
        fallback,
      );
      const sel = (await resp.json()) as VoiceSelection;
      const source = sel.source_name?.trim();
      return {
        ...sel,
        source_name: source && !isVoicePlatformLabel(source) ? source : null,
      };
    },
  });
  return toSectionQuery(q, fallback);
}

export function useVoicesQuery(enabled = true) {
  const key = useKey('store-agent-voices');
  const fallback = 'Voice choices are temporarily unavailable.';
  const q = useQuery({
    queryKey: key,
    enabled,
    queryFn: async (): Promise<VoiceOption[]> => {
      const resp = await ensureOk(
        await api.get(apiEndpoints.getStoreAgentVoices()),
        fallback,
      );
      const data = await resp.json();
      return (Array.isArray(data?.voices) ? data.voices : []).map(
        customerFacingVoice,
      );
    },
  });
  return toSectionQuery(q, fallback);
}

export function useVoiceActions() {
  const queryClient = useQueryClient();
  const key = useKey('store-agent-voice');
  const save = useCallback(
    async (voiceId: string | null) => {
      await ensureOk(
        await api.patch(apiEndpoints.updateStoreAgentVoice(), {
          voice_id: voiceId,
        }),
        'Could not update the store voice.',
      );
      await queryClient.invalidateQueries({ queryKey: key });
    },
    [queryClient, key],
  );
  return useMemo(() => ({ save }), [save]);
}

// ---------------------------------------------------------------------------
// Google place (platform admin)
// ---------------------------------------------------------------------------

export function useGooglePlaceQuery(enabled: boolean) {
  const key = useKey('store-google-place');
  const fallback = 'The linked Google place could not be loaded.';
  const q = useQuery({
    queryKey: key,
    enabled,
    queryFn: async (): Promise<GooglePlaceLink | null> => {
      const resp = await ensureOk(
        await api.get(apiEndpoints.getStoreGooglePlace()),
        fallback,
      );
      const data = (await resp.json()) as Partial<GooglePlaceLink> | null;
      return data?.place_id ? (data as GooglePlaceLink) : null;
    },
  });
  return toSectionQuery(q, fallback);
}

export function useGoogleActions() {
  const queryClient = useQueryClient();
  const key = useKey('store-google-place');
  const hours = useHoursActions();
  return useMemo(
    () => ({
      search: async (query: string): Promise<GooglePlaceLink[]> => {
        const resp = await ensureOk(
          await api.post(apiEndpoints.searchStoreGooglePlace(), { query }),
          'Google place search failed.',
        );
        const body = await resp.json();
        return Array.isArray(body?.results) ? body.results : [];
      },
      link: async (placeId: string) => {
        const resp = await ensureOk(
          await api.put(apiEndpoints.updateStoreGooglePlace(), {
            place_id: placeId,
          }),
          'Could not update the Google place.',
        );
        const linked = (await resp.json()) as GooglePlaceLink;
        queryClient.setQueryData(key, linked);
        hours.refreshHours();
        return linked;
      },
    }),
    [queryClient, key, hours],
  );
}

// ---------------------------------------------------------------------------
// OpenTable area blackouts
// ---------------------------------------------------------------------------

export interface OpenTableData {
  hasActiveConnection: boolean;
  areas: DiningArea[];
  blackouts: AreaBlackout[];
}

export function useOpenTableQuery(): SectionQuery<OpenTableData> {
  const areasKey = useKey('opentable-dining-areas');
  const blackoutsKey = useKey('opentable-area-blackouts');
  const fallback = 'Could not load OpenTable blackout settings.';
  const areas = useQuery({
    queryKey: areasKey,
    queryFn: async (): Promise<ListResponse<DiningArea>> => {
      const resp = await ensureOk(
        await api.get(apiEndpoints.openTableDashboardDiningAreas()),
        fallback,
      );
      return resp.json();
    },
  });
  const blackouts = useQuery({
    queryKey: blackoutsKey,
    queryFn: async (): Promise<ListResponse<AreaBlackout>> => {
      const resp = await ensureOk(
        await api.get(apiEndpoints.openTableDashboardAreaBlackouts()),
        fallback,
      );
      return resp.json();
    },
  });
  return combineQueries(
    [toSectionQuery(areas, fallback), toSectionQuery(blackouts, fallback)],
    () => ({
      hasActiveConnection: areas.data?.has_active_connection !== false,
      areas: areas.data?.results ?? [],
      blackouts: blackouts.data?.results ?? [],
    }),
    fallback,
  );
}

export interface BlackoutBody {
  starts_at_local: string;
  ends_at_local: string;
  all_day: boolean;
  reason: string;
  dining_area_ids: number[];
}

export function useBlackoutActions() {
  const queryClient = useQueryClient();
  const key = useKey('opentable-area-blackouts');
  return useMemo(() => {
    const refresh = () => queryClient.invalidateQueries({ queryKey: key });
    return {
      create: async (body: BlackoutBody) => {
        await ensureOk(
          await api.post(apiEndpoints.openTableDashboardAreaBlackouts(), body),
          'Could not save the blackout.',
        );
        await refresh();
      },
      update: async (id: number, body: BlackoutBody) => {
        await ensureOk(
          await api.patch(
            apiEndpoints.openTableDashboardAreaBlackoutDetail(id),
            body,
          ),
          'Could not save the blackout.',
        );
        await refresh();
      },
      delete: async (id: number) => {
        await ensureOk(
          await api.delete(
            apiEndpoints.openTableDashboardAreaBlackoutDetail(id),
          ),
          'Could not delete the blackout.',
        );
        await refresh();
      },
    };
  }, [queryClient, key]);
}

// ---------------------------------------------------------------------------
// Notifications
// ---------------------------------------------------------------------------

export interface NotificationsData {
  targets: NotificationTarget[];
  rules: NotificationRule[];
  eventTypes: EventTypeOption[];
}

export function useNotificationsQuery(
  storeId: number | undefined,
): SectionQuery<NotificationsData> {
  const key = useKey('store-notifications', storeId ?? null);
  const fallback = 'Notifications could not be loaded.';
  const q = useQuery({
    queryKey: key,
    enabled: storeId != null,
    queryFn: async (): Promise<NotificationsData> => {
      const [targets, rules, events] = await Promise.all([
        api.get(apiEndpoints.getNotificationTargets(storeId)),
        api.get(apiEndpoints.getNotificationRules(storeId)),
        api.get(
          apiEndpoints.getNotificationEventTypes(storeId, {
            fullCatalog: true,
          }),
        ),
      ]);
      await ensureOk(targets, 'Failed to load contacts.');
      await ensureOk(rules, 'Failed to load notifications.');
      await ensureOk(events, 'Failed to load event types.');
      return {
        targets: asList<NotificationTarget>(await targets!.json()),
        rules: asList<NotificationRule>(await rules!.json()),
        eventTypes: asList<EventTypeOption>(await events!.json()).map((e) =>
          e.value === STAFF_MESSAGE ? { ...e, label: 'Voicemails' } : e,
        ),
      };
    },
  });
  return toSectionQuery(q, fallback);
}

export interface CreateRulePayload {
  store: number;
  event_types: string[];
  is_enabled: true;
  target_ids?: number[];
  contact?: {
    channel: Channel;
    label: string;
    email: string | null;
    phone_e164: string | null;
    is_enabled: true;
  };
  applies_to_all_voicemails?: boolean;
}

export function useNotificationActions(storeId: number | undefined) {
  const queryClient = useQueryClient();
  const key = useKey('store-notifications', storeId ?? null);
  return useMemo(() => {
    const refresh = () => queryClient.invalidateQueries({ queryKey: key });
    return {
      refresh,
      createRules: async (payload: CreateRulePayload) => {
        await ensureOk(
          await api.post(apiEndpoints.createNotificationRule(), payload),
          'Failed to save the notification.',
        );
      },
      /** Unlinks one contact; deletes the rule when no contacts would remain. */
      unlinkTarget: async (rule: NotificationRule, targetId: number) => {
        const remaining = (rule.targets || [])
          .map((t) => t.id)
          .filter(
            (id): id is number => typeof id === 'number' && id !== targetId,
          );
        await ensureOk(
          remaining.length === 0
            ? await api.delete(apiEndpoints.deleteNotificationRule(rule.id))
            : await api.patch(apiEndpoints.updateNotificationRule(rule.id), {
                target_ids: remaining,
              }),
          'Failed to update a notification.',
        );
      },
      patchTarget: async (
        targetId: number,
        patch: Partial<Pick<NotificationTarget, 'is_enabled'>>,
      ) => {
        await ensureOk(
          await api.patch(
            apiEndpoints.updateNotificationTarget(targetId),
            patch,
          ),
          'Failed to update the contact.',
        );
      },
    };
  }, [queryClient, key]);
}
